import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { Link } from "react-router-dom";

const principles = [
  {
    icon: "ri-eye-off-line",
    title: "只用公开素材",
    desc: "所有对照图片都来自博主本人公开发布的内容，不使用非公开渠道获得的照片，也不做画面篡改。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "描述就事论事",
    desc: "我们聊造型、妆容、配色、镜头表现，不评价五官好不好看，也不使用贬损性的措辞。",
  },
  {
    icon: "ri-link-m",
    title: "标注可追溯",
    desc: "每一组对比都标明早期与近期的年份节点，方便读者自行核对信息来源与时间线。",
  },
  {
    icon: "ri-edit-circle-line",
    title: "接受更正",
    desc: "当事人或读者指出年份、账号对应有误时，我们核实后会第一时间修正并保留修改记录。",
  },
];

const milestones = [
  { year: "2021", title: "从一个疑问开始", desc: "起初只是好奇：那些看起来判若两人的博主，中间到底发生了什么？" },
  { year: "2022", title: "建立整理规范", desc: "确定了公开素材、四维评分、时间线标注的基础口径，避免内容变成单纯的围观。" },
  { year: "2024", title: "扩展收录范围", desc: "从美妆穿搭延伸到知识、健身、数码等方向，看到不同领域的形象养成路径。" },
  { year: "2025", title: "进入持续更新", desc: "已收录对象跟随后续公开露面补档，形成一个会生长的形象变化档案库。" },
];

const voices = [
  {
    name: "阿茶不加糖",
    role: "三年老读者",
    text: "以前考古要翻好几年的主页，现在打开一组就能看到前后，省事太多了。而且标注了年份，看的时候心里更有数。",
  },
  {
    name: "拍照的栗子",
    role: "摄影爱好者",
    text: "我最喜欢看它讲构图和打光的部分，不只是看热闹，还能偷师一点造型和拍摄思路。",
  },
  {
    name: "夜行小周",
    role: "内容运营",
    text: "做内容的人看这个很有共鸣，从出租屋到专业布光那几步，几乎每个创作者都走过。",
  },
];

export default function About() {
  const aboutJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于反差档案",
      description:
        "反差档案是一个以公开信息整理为主的网红形象变化观察站，介绍品牌定位、内容原则与收录方式。",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: "反差档案",
        url: "https://fancha-archive.example",
        email: "hello@fancha-archive.example",
        description: "整理网红前后形象对比内容，记录创作者的风格成长过程。",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fancha-archive.example/" },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="关于我们 - 反差档案的内容原则与收录方式"
        description="了解反差档案的定位、收录口径与内容原则：只用公开素材、就事论事描述、标注可追溯、接受更正，做一份会生长的网红形象变化档案。"
        keywords="关于反差档案,网红反差内容站,网红形象对比原则,网红反差收录方式"
        path="/about"
        ogType="website"
        jsonLd={aboutJsonLd}
      />

      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <p className="mt-6 text-xs tracking-[0.24em] text-primary-600">ABOUT US</p>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              关于反差档案：我们把"变好看了"拆成了"怎么一步步变的"
            </h1>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              反差档案是一个专注整理网络创作者形象变化的观察站。我们不讨论谁美谁丑，
              只想把那些公开发生过的画面按顺序摆好，让变化这件事本身被看清楚。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <div>
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                网站定位
              </h2>
              <div className="mt-5 flex flex-col gap-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                <p>
                  很多人提到"网红反差"，第一反应是比较和评判。但在反差档案里，我们更愿意把它当成一份成长档案：
                  同一个创作者在不同阶段，造型、妆容、镜头感各是什么状态，中间经历了哪些明显的转折点。
                </p>
                <p>
                  所以本站的每一组内容都由三部分组成——前后两组公开画面、一条按年份排列的时间线，
                  以及一段尽量克制的文字解读。读者可以只看图，也可以顺着时间线理解变化的脉络。
                </p>
                <p>
                  我们不追求收录数量上的爆发式增长，更在意每一组内容是否能经得起核对。
                  目前以美妆护肤、穿搭时尚、知识科普、健身运动、旅行户外等方向为主，后续会随读者需求继续扩展。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-xl font-black text-primary-600">312</p>
                  <p className="mt-1 text-xs text-foreground-600">已收录对比对象</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-xl font-black text-primary-600">4 个</p>
                  <p className="mt-1 text-xs text-foreground-600">覆盖内容平台</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-xl font-black text-primary-600">每日</p>
                  <p className="mt-1 text-xs text-foreground-600">跟随后续更新</p>
                </div>
              </div>
            </div>

            <aside className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-black text-foreground-950">发展理念</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                我们不希望这里成为一个容貌评审现场。一个人愿意持续更新内容、逐步找到适合自己的风格，
                这件事本身就值得记录。
              </p>
              <ul className="mt-6 flex flex-col gap-4">
                <li className="flex gap-3">
                  <i className="ri-seedling-line mt-0.5 text-lg text-secondary-600"></i>
                  <p className="text-sm text-foreground-700">
                    看变化，不看高下。评分只用于横向对比，不针对个人。
                  </p>
                </li>
                <li className="flex gap-3">
                  <i className="ri-history-line mt-0.5 text-lg text-secondary-600"></i>
                  <p className="text-sm text-foreground-700">
                    时间线优先于结论。先呈现过程，再谈感受。
                  </p>
                </li>
                <li className="flex gap-3">
                  <i className="ri-user-heart-line mt-0.5 text-lg text-secondary-600"></i>
                  <p className="text-sm text-foreground-700">
                    尊重当事人。任何一位创作者都可以联系我们调整或删除相关内容。
                  </p>
                </li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              内容原则
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              这四条是我们做每一次整理时都会回看的底线，也是读者判断本站内容是否可信的依据。
            </p>
            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((item) => (
                <li
                  key={item.title}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
              <div>
                <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  我们走到这里
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  从一个人的好奇，到一套被反复使用的整理规范，中间也有过好几次调整。
                </p>
              </div>
              <ol className="flex flex-col divide-y divide-background-200 border-y border-background-200">
                {milestones.map((item) => (
                  <li key={item.year} className="flex gap-5 py-5">
                    <span className="font-heading text-lg font-black text-primary-600">
                      {item.year}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-secondary-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              读者怎么说
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((voice) => (
                <figure
                  key={voice.name}
                  className="flex h-full flex-col rounded-lg border border-secondary-200 bg-background-50 p-5"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {voice.text}
                  </blockquote>
                  <figcaption className="mt-5 border-t border-background-200 pt-4">
                    <p className="font-heading text-sm font-bold text-foreground-950">{voice.name}</p>
                    <p className="mt-1 text-xs text-foreground-500">{voice.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-4 py-12 md:px-8 md:py-16 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                联系方式
              </h2>
              <ul className="mt-5 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-base text-primary-500"></i>
                  hello@fancha-archive.example
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-chat-3-line text-base text-primary-500"></i>
                  线索提交与合作沟通统一走联系页面表单
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-base text-primary-500"></i>
                  工作日 10:00 - 18:00，通常 24 小时内回复
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line"></i>
                前往联系页面
              </Link>
              <Link
                to="/bang"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-950 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
              >
                继续浏览榜单
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}